import { API } from '../api.js';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeSwitcher from '../components/ThemeSwitcher';
import MarkdownRenderer from '../components/MarkdownRenderer';

// Thumbnail mock images (coloured placeholders)
const THUMB_COLORS = ['#1e3a5f','#1a3520','#2d1a00','#1a1a3d','#1a0a2e'];

export default function CommunityPublicPage({ community: c, onJoin }) {
  const navigate            = useNavigate();
  const { user, token }     = useAuth();
  const [joining,  setJoining]  = useState(false);
  const [joined,   setJoined]   = useState(false);
  const [activeThumb, setActiveThumb] = useState(0);

  const isPaid    = c.pricing === 'paid' && c.price > 0;
  const joinLabel = isPaid ? `JOIN $${c.price}/month` : 'JOIN FOR FREE';

  async function handleJoin() {
    if (!user) { navigate('/login'); return; }
    setJoining(true);
    try {
      const res = await fetch(`${API}/api/communities/${c.id}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setJoined(true);
        setTimeout(() => onJoin(), 600); // Short delay for animation
      }
    } catch {}
    setJoining(false);
  }

  const features = Array.isArray(c.features) ? c.features : [];

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', fontFamily:'inherit' }}>

      {/* ── Navbar ── */}
      <nav style={{ background:'var(--bg)', borderBottom:'1px solid var(--border)', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ maxWidth:1160, margin:'0 auto', padding:'0 24px', height:60, display:'flex', alignItems:'center', gap:12 }}>
          <div onClick={() => navigate('/')}
            style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontWeight:900, fontSize:18, color:'var(--text)' }}>
            <div style={{ width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
              <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            Qeduvo
            <span style={{ fontWeight:400, fontSize:13, color:'var(--text3)' }}>▾</span>
          </div>
          <div style={{ flex:1 }} />
          <ThemeSwitcher />
          {user ? (
            <button onClick={() => navigate('/app')}
              style={{ padding:'7px 16px', borderRadius:8, border:'none', background:'var(--accent)', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>
              My Community
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/login')}
                style={{ padding:'7px 16px', borderRadius:8, border:'1px solid var(--border)', background:'transparent', color:'var(--text)', cursor:'pointer', fontSize:13, fontWeight:600 }}>Log in</button>
              <button onClick={() => navigate('/signup')}
                style={{ padding:'7px 16px', borderRadius:8, border:'none', background:'var(--accent)', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>Get started</button>
            </>
          )}
        </div>
      </nav>

      {/* ── Main layout (matches Qeduvo about page) ── */}
      <div className="public-layout">

        {/* ── LEFT column ── */}
        <div>

          {/* Video player area */}
          <div style={{ borderRadius:14, overflow:'hidden', background:c.color || '#0a1628', position:'relative', aspectRatio:'16/9', marginBottom:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={() => {}}>
            {(() => {
              const images = Array.isArray(c.aboutImages) && c.aboutImages.length > 0 ? c.aboutImages : [];
              const currentImage = images.length > 0 ? images[activeThumb] : (c.coverImageUrl || null);
              if (currentImage) {
                return <img src={`${API}${currentImage}`} alt="About display" style={{ width:'100%', height:'100%', objectFit:'cover', position:'absolute', inset:0 }} />;
              }
              return (
                <>
                  {/* Gradient overlay */}
                  <div style={{ position:'absolute', inset:0, background:`linear-gradient(135deg, ${c.color||'#0a1628'}, ${c.color||'#0a1628'}88)` }} />
                  {/* Emoji big */}
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:80, opacity:0.15 }}>{c.emoji}</div>
                </>
              );
            })()}
            {/* Play button */}
            {(!c.aboutImages || c.aboutImages.length === 0) && (
              <div style={{ position:'relative', zIndex:2, width:72, height:72, borderRadius:'50%', background:'rgba(255,255,255,0.95)', display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 8px 32px rgba(0,0,0,0.3)', cursor:'pointer', transition:'transform 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.transform='scale(1.08)'}
                onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>
                <div style={{ width:0, height:0, borderStyle:'solid', borderWidth:'14px 0 14px 24px', borderColor:'transparent transparent transparent #1a1a2e', marginLeft:4 }} />
              </div>
            )}
            {/* Duration */}
            {(!c.aboutImages || c.aboutImages.length === 0) && (
              <div style={{ position:'absolute', bottom:14, left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.7)', color:'#fff', fontSize:12, padding:'5px 14px', borderRadius:20, zIndex:2 }}>
                Powered by 🎬 · 3 min
              </div>
            )}
          </div>

          {/* Thumbnail strip */}
          <div style={{ display:'flex', gap:8, marginBottom:28 }}>
            {(() => {
              const images = Array.isArray(c.aboutImages) && c.aboutImages.length > 0 ? c.aboutImages : [];
              if (images.length > 0) {
                return images.map((imgUrl, i) => (
                  <div key={i} onClick={() => setActiveThumb(i)}
                    style={{ flex: 1, aspectRatio:'16/10', borderRadius:8, cursor:'pointer', position:'relative', overflow:'hidden',
                      outline: activeThumb === i ? '3px solid var(--accent)' : '2px solid transparent', transition:'outline 0.15s' }}>
                    <img src={`${API}${imgUrl}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </div>
                ));
              } else {
                return THUMB_COLORS.map((bg, i) => (
                  <div key={i} onClick={() => setActiveThumb(i)}
                    style={{ flex:1, aspectRatio:'16/10', borderRadius:8, background:bg, cursor:'pointer', position:'relative', overflow:'hidden',
                      outline: activeThumb === i ? '3px solid var(--accent)' : '2px solid transparent', transition:'outline 0.15s' }}>
                    {i === 0 && (
                      <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <div style={{ width:24, height:24, borderRadius:'50%', background:'rgba(255,255,255,0.8)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>▶</div>
                      </div>
                    )}
                    <div style={{ position:'absolute', inset:0, background:`linear-gradient(135deg, ${bg}, ${bg}aa)` }} />
                    <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, opacity:0.3 }}>{c.emoji}</div>
                  </div>
                ));
              }
            })()}
          </div>

          {/* Meta badges row */}
          <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:16, marginBottom:24, paddingBottom:24, borderBottom:'1px solid var(--border)' }}>
            {c.is_private ? (
              <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, color:'var(--text2)', fontWeight:600 }}>
                <span>🔒</span> Private
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, color:'var(--text2)', fontWeight:600 }}>
                <span>🌍</span> Public
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, color:'var(--text2)', fontWeight:600 }}>
              <span>👥</span> {(c.member_count ?? 0).toLocaleString()} members
            </div>
            {isPaid && (
              <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, color:'var(--text2)', fontWeight:600 }}>
                <span>💰</span> ${c.price}/month
              </div>
            )}
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:14, color:'var(--text2)', fontWeight:600 }}>
              <span>👤</span> By <strong>{c.owner_name}</strong> 💎 ⭐
            </div>
          </div>

          {/* Highlight badges */}
          {c.highlight && (
            <div style={{ marginBottom:20 }}>
              {c.highlight.split('·').map(h => h.trim()).filter(Boolean).map((h, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, fontSize:14, color:'var(--text2)' }}>
                  <span>{i === 0 ? '⭐' : '🏅'}</span> {h}
                </div>
              ))}
            </div>
          )}

          {/* Description / Note */}
          {c.note ? (
            <div style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 24 }}>
              <MarkdownRenderer text={c.note} />
            </div>
          ) : (
            c.description && <div style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 24 }}>{c.description}</div>
          )}

          {/* What's inside */}
          {features.length > 0 && (
            <div>
              <div style={{ fontSize:16, fontWeight:800, marginBottom:16, color:'var(--text)' }}>What's inside? 🚀</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {features.map((f, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 16px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10 }}>
                    <span style={{ color:'var(--accent3)', fontSize:16, flexShrink:0 }}>✓</span>
                    <span style={{ fontSize:14, color:'var(--text2)', lineHeight:1.5 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT column (info card — matches Qeduvo exactly) ── */}
        <div style={{ position:'sticky', top:80 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, overflow:'hidden', boxShadow:'var(--shadow)' }}>

            {/* Community banner image */}
            <div style={{
              height: 160,
              background: c.coverImageUrl
                ? `url(${API}${c.coverImageUrl}) center/cover no-repeat`
                : `linear-gradient(135deg, ${c.color||'#0a1628'}, ${c.color||'#0a1628'}88)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {!c.coverImageUrl && <div style={{ fontSize:56, opacity:0.3 }}>{c.emoji}</div>}
            </div>

            {/* Info body */}
            <div style={{ padding:'20px 20px 24px' }}>
              <div style={{ fontWeight:800, fontSize:18, marginBottom:3, color:'var(--text)' }}>{c.name}</div>
              <div style={{ fontSize:12, color:'var(--text3)', marginBottom:14 }}>qeduvo.com/{c.id}</div>

              <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6, marginBottom:20 }}>
                {c.description.length > 120 ? c.description.slice(0, 120) + '…' : c.description}
              </div>

              {/* Stats row */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', textAlign:'center', marginBottom:20, paddingBottom:20, borderBottom:'1px solid var(--border)' }}>
                {[
                  { val: (c.member_count ?? 0).toLocaleString(), label:'Members' },
                  { val: (c.online_count ?? 0).toLocaleString(), label:'Online' },
                  { val: (c.admin_count ?? 0).toString(),        label:'Admins' },
                ].map(s => (
                  <div key={s.label}>
                    <div style={{ fontSize:18, fontWeight:900, color:'var(--text)' }}>{s.val}</div>
                    <div style={{ fontSize:11, color:'var(--text3)' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* JOIN button */}
              {isPaid && !joined && (
                <div style={{ fontSize:12, color:'var(--accent2)', fontWeight:600, textAlign:'center', marginBottom:8 }}>
                  ⚠️ Only a few spots remaining at this price
                </div>
              )}

              <button onClick={handleJoin} disabled={joining || joined}
                style={{ width:'100%', padding:'14px', borderRadius:10, border:'none', cursor: (joining||joined) ? 'default' : 'pointer',
                  fontWeight:900, fontSize:16, letterSpacing:0.5, transition:'all 0.2s',
                  background: joined ? 'var(--accent3)' : isPaid ? '#eab308' : 'var(--accent)',
                  color: joined ? '#fff' : isPaid ? '#000' : '#fff',
                  opacity: joining ? 0.8 : 1,
                  boxShadow: joined ? 'none' : isPaid ? '0 4px 20px rgba(234,179,8,0.4)' : '0 4px 20px rgba(99,91,255,0.35)' }}>
                {joined ? '✓ JOINED!' : joining ? 'Joining…' : joinLabel}
              </button>

              {/* Powered by */}
              <div style={{ textAlign:'center', marginTop:18, fontSize:12, color:'var(--text3)' }}>
                Powered by <span style={{ fontWeight:900, color:'var(--accent)' }}>Qeduvo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
